import { useCallback } from 'react';
import { mobilePlotArea, mobileLayerStock, mobileStandTotals } from '../../../utils/mobileCalculations';

// Хук для расчета площади выдела (аналогично Form4)
export const usePloshadVydel = (form1Data, dataType) => {
  const calculatePloshadVydel = useCallback(() => mobilePlotArea(form1Data, dataType), [form1Data, dataType]);

  return { calculatePloshadVydel };
};

// Хук для расчета итогов по ярусам
export const useYarusTotals = (formData, calculateDamage_0_func, calculateDamagePercentage_func) => {
  const calculateYarusTotals = useCallback((yarusNumber, column) => {
    if (!formData?.data) return 0;
    
    // Проверяем, что data является массивом
    const dataArray = Array.isArray(formData.data) ? formData.data : [];
    const yarusData = dataArray.filter(item => item.yarus === yarusNumber.toString() || item.yarus === yarusNumber);
    if (yarusData.length === 0) return 0;
    
    const total = yarusData.reduce((sum, item) => {
      let value = 0;
      
      if (column === 'objedola0') {
        value = calculateDamage_0_func(item);
      } else if (column === 'dolapovr') {
        value = calculateDamagePercentage_func(item);
      } else {
        value = Number(item[column]) || 0;
      }
      
      const dolap = Number(item.dolap) || 0;
      return sum + (value * dolap / 10);
    }, 0);
    
    return total;
  }, [formData, calculateDamage_0_func, calculateDamagePercentage_func]);

  return { calculateYarusTotals };
};

// Хук для расчета общей суммы плотностей (аналогично Form4)
export const useTotalDensitySum = (form1Data, independentForm2Data, calculatePloshadVydel) => {
  const calculateTotalDensitySum = useCallback(() =>
    (independentForm2Data?.yarusy_dependent || []).reduce((sum, tier) => sum + mobileLayerStock(tier, calculatePloshadVydel()), 0),
    [independentForm2Data, calculatePloshadVydel]
  );

  return { calculateTotalDensitySum };
};

// Хук для расчета итогов по насаждению (аналогично Form4)
export const useTotalsByNasazhdeniya = (
  formData,
  independentForm2Data,
  form1Data,
  calculatePloshadVydel,
  calculateTotalDensitySum,
  calculateYarusTotals,
  calculateDamage_0_func,
  calculateDamagePercentage_func
) => {
  const calculateTotalsByNasazhdeniya = useCallback(
    () => mobileStandTotals(formData, independentForm2Data, calculatePloshadVydel(), 5),
    [formData, independentForm2Data, calculatePloshadVydel]
  );

  return { calculateTotalsByNasazhdeniya };
};

// Хук для расчета итогов по таблице расчетов (для обратной совместимости)
export const useDamageCalculations = (displayData, calculateDamage_0_func, calculateDamagePercentage_func, calculateTotalsByNasazhdeniya) => {
  // Если есть функция для расчета итогов по насаждению, используем её
  const calculateTotalDamage_0 = useCallback(() => {
    if (calculateTotalsByNasazhdeniya) {
      const totals = calculateTotalsByNasazhdeniya();
      return totals.objedola0 || '0.00';
    }
    const dataArray = Array.isArray(displayData.data) ? displayData.data : [];
    return dataArray.reduce((sum, item) => {
      const damage_0_value = calculateDamage_0_func(item);
      const dolap = Number(item.dolap) || 0;
      return sum + (damage_0_value * dolap / 10);
    }, 0).toFixed(2);
  }, [displayData.data, calculateDamage_0_func, calculateTotalsByNasazhdeniya]);

  const calculateTotalDamage_1_25 = useCallback(() => {
    if (calculateTotalsByNasazhdeniya) {
      const totals = calculateTotalsByNasazhdeniya();
      return totals.objedola25 || '0.00';
    }
    const dataArray = Array.isArray(displayData.data) ? displayData.data : [];
    return dataArray.reduce((sum, item) => {
      const value = Number(item.objedola25) || 0;
      const dolap = Number(item.dolap) || 0;
      return sum + (value * dolap / 10);
    }, 0).toFixed(2);
  }, [displayData.data, calculateTotalsByNasazhdeniya]);

  const calculateTotalDamage_25_1_50 = useCallback(() => {
    if (calculateTotalsByNasazhdeniya) {
      const totals = calculateTotalsByNasazhdeniya();
      return totals.objedola50 || '0.00';
    }
    const dataArray = Array.isArray(displayData.data) ? displayData.data : [];
    return dataArray.reduce((sum, item) => {
      const value = Number(item.objedola50) || 0;
      const dolap = Number(item.dolap) || 0;
      return sum + (value * dolap / 10);
    }, 0).toFixed(2);
  }, [displayData.data, calculateTotalsByNasazhdeniya]);

  const calculateTotalDamage_50_1_75 = useCallback(() => {
    if (calculateTotalsByNasazhdeniya) {
      const totals = calculateTotalsByNasazhdeniya();
      return totals.objedola75 || '0.00';
    }
    const dataArray = Array.isArray(displayData.data) ? displayData.data : [];
    return dataArray.reduce((sum, item) => {
      const value = Number(item.objedola75) || 0;
      const dolap = Number(item.dolap) || 0;
      return sum + (value * dolap / 10);
    }, 0).toFixed(2);
  }, [displayData.data, calculateTotalsByNasazhdeniya]);

  const calculateTotalDamage_75_1_100 = useCallback(() => {
    if (calculateTotalsByNasazhdeniya) {
      const totals = calculateTotalsByNasazhdeniya();
      return totals.objedola100 || '0.00';
    }
    const dataArray = Array.isArray(displayData.data) ? displayData.data : [];
    return dataArray.reduce((sum, item) => {
      const value = Number(item.objedola100) || 0;
      const dolap = Number(item.dolap) || 0;
      return sum + (value * dolap / 10);
    }, 0).toFixed(2);
  }, [displayData.data, calculateTotalsByNasazhdeniya]);

  const calculateTotalDamagePercentage = useCallback(() => {
    if (calculateTotalsByNasazhdeniya) {
      const totals = calculateTotalsByNasazhdeniya();
      return totals.dolapovr || '0.00';
    }
    const dataArray = Array.isArray(displayData.data) ? displayData.data : [];
    return dataArray.reduce((sum, item) => {
      const damagePercentage = calculateDamagePercentage_func(item);
      const dolap = Number(item.dolap) || 0;
      return sum + (damagePercentage * dolap / 10);
    }, 0).toFixed(2);
  }, [displayData.data, calculateDamagePercentage_func, calculateTotalsByNasazhdeniya]);

  return {
    calculateTotalDamage_0,
    calculateTotalDamage_1_25,
    calculateTotalDamage_25_1_50,
    calculateTotalDamage_50_1_75,
    calculateTotalDamage_75_1_100,
    calculateTotalDamagePercentage
  };
};

